/**
 * Select — the `Input` of the drop-down world (`MCK-20260521-003` Tier 1).
 *
 * Thirty-seven files were drawing a bare `<select>` with a `.select` class of their own, so a
 * dropdown looked like whatever the last screen decided and its label was a `<span>` above it,
 * a `<label>` around it, or an `aria-label` — three different answers to *"what is this
 * control called"*, one of which is not announced at all. This is the one answer, and it is
 * `Input`'s: the label is not optional, `error` renders through `InlineError` and wires
 * `aria-invalid` + `aria-describedby`, and the geometry is the same 40px field.
 *
 * **A native `<select>`, deliberately.** A listbox built out of divs has to re-implement type
 * ahead, the mobile wheel, the platform keyboard and the way a long list scrolls, and the
 * product already carries two richer controls — `ListboxPopup` and `ChipSelect` — for the
 * cases that genuinely need more (search, multi-select, chips). This is the plain one, and
 * plain is what most of those thirty-seven were.
 *
 * **`labelHidden` is for a toolbar, not for a form.** A control in a filter bar is named by
 * what sits beside it and a visible micro-label above every one of them would double the bar's
 * height; the label still exists and is still announced. A form field keeps its label visible —
 * a placeholder disappears the moment somebody chooses, which is exactly when a person coming
 * back to a half-filled form needs it.
 *
 * The chevron is drawn by this file rather than by the platform: `appearance: none` is what
 * makes a select look the same on three operating systems, and a control that looks native on
 * one and foreign on the other two is the inconsistency this component exists to end.
 */

import type { ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";

import { ChevronDown } from "lucide-react";

import { InlineError } from "./InlineError";
import type { InputTone } from "./Input";
import styles from "./Select.module.css";

/** `md` is the form field; `sm` is the toolbar's, which sits beside buttons of that height. */
export type SelectSize = "md" | "sm";

export interface SelectProps
  extends Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    // `size` is taken: on a native `<select>` it is *how many rows are visible at once*, which
    // is a different control (a list box) and one nothing here draws. This component's `size`
    // is the field's height, the way `Button`'s is.
    "className" | "id" | "children" | "size"
  > {
  /** What this control is called. Required — never a placeholder, never nothing. */
  readonly label: string;
  /**
   * Draw the label as an accessible name only. For a control whose surroundings already say
   * what it is: a filter bar, a toolbar, a table cell.
   */
  readonly labelHidden?: boolean;
  readonly tone?: InputTone;
  readonly size?: SelectSize;
  /** Rendered under the field, and announced. Its presence is what marks the field invalid. */
  readonly error?: string | undefined;
  /** Non-error helper text, replaced by `error` when there is one. */
  readonly hint?: ReactNode;
  /** The `<option>`s. Kept as children so a call site's own list is unchanged. */
  readonly children: ReactNode;
}

export function Select({
  label,
  labelHidden = false,
  tone = "light",
  size = "md",
  error,
  hint,
  children,
  ...rest
}: SelectProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const invalid = error !== undefined && error !== "";

  const classes = [
    styles.field,
    styles[tone],
    styles[size],
    invalid ? styles.invalid : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <label
        className={labelHidden ? styles.srOnly : styles.label}
        htmlFor={id}
      >
        {label}
      </label>
      <div className={styles.selectWrap}>
        <select
          {...rest}
          id={id}
          className={styles.select}
          aria-invalid={invalid || undefined}
          aria-describedby={
            invalid || hint !== undefined ? messageId : undefined
          }
        >
          {children}
        </select>
        {/* Inside the field and out of the way of the pointer, so a click anywhere on the
            control still opens it — a chevron that swallowed the press would be a decoration
            that broke the thing it decorates. */}
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          className={styles.chevron}
          aria-hidden
        />
      </div>
      {invalid ? (
        <InlineError id={messageId}>{error}</InlineError>
      ) : hint !== undefined ? (
        <p className={styles.hint} id={messageId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
